import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Project, Client, OwnerProfile } from '../page';

interface ProjectModalProps {
  project: Project | null;
  clients: Client[];
  owners: OwnerProfile[];
  preselectedClientId: string | null;
  onClose: () => void;
  onSave: (data: Partial<Project>) => Promise<void>;
}

interface UserProfile { id: string; full_name: string; email: string; }

export default function ProjectModal({ project, clients, owners, preselectedClientId, onClose, onSave }: ProjectModalProps) {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'dados' | 'membros'>('dados');
  const [clientUsers, setClientUsers] = useState<UserProfile[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [form, setForm] = useState({
    client_id: '',
    name: '',
    description: '',
    owner_id: '',
    start_date: '',
    due_date: '',
    status: 'planejamento',
    scrum_active: false,
    methodology: 'kanban',
    total_value: '',
    estimated_hours: '',
    is_active: true,
  });

  useEffect(() => {
    if (project) {
      setForm({
        client_id: project.client_id || '',
        name: project.name || '',
        description: project.description || '',
        owner_id: project.owner_id || '',
        start_date: project.start_date || '',
        due_date: project.due_date || '',
        status: project.status || 'planejamento',
        scrum_active: project.scrum_active || false,
        methodology: project.methodology || 'kanban',
        total_value: project.total_value != null ? String(project.total_value) : '',
        estimated_hours: project.estimated_hours != null ? String(project.estimated_hours) : '',
        is_active: project.is_active ?? true,
      });
      loadCurrentMembers(project.id);
    } else if (preselectedClientId) {
      setForm((prev) => ({ ...prev, client_id: preselectedClientId }));
    }
  }, [project, preselectedClientId]);

  useEffect(() => {
    const cid = form.client_id;
    if (!cid) { setClientUsers([]); return; }
    supabase
      .from('client_users')
      .select('user_id, profile:profiles(id, full_name, email)')
      .eq('client_id', cid)
      .eq('is_active', true)
      .then(({ data }) => {
        const profiles = (data || []).map((d: any) => d.profile).filter(Boolean) as UserProfile[];
        setClientUsers(profiles);
      });
  }, [form.client_id]);

  const loadCurrentMembers = async (projectId: string) => {
    const { data } = await supabase.from('project_members').select('user_id').eq('project_id', projectId);
    setSelectedMembers((data || []).map((m) => m.user_id));
  };

  const set = (field: string, value: string | boolean) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleMember = (userId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.client_id) return;
    setSaving(true);

    const payload: Partial<Project> = {
      client_id: form.client_id,
      name: form.name,
      description: form.description || null,
      owner_id: form.owner_id || null,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      status: form.status,
      scrum_active: form.scrum_active,
      methodology: form.scrum_active ? 'scrum' : form.methodology,
      total_value: form.total_value ? parseFloat(form.total_value) : null,
      estimated_hours: form.estimated_hours ? parseInt(form.estimated_hours, 10) : null,
      is_active: form.is_active,
    };

    await onSave(payload);

    // Salvar membros se for novo projeto ou edição
    if (project) {
      await supabase.from('project_members').delete().eq('project_id', project.id);
      if (selectedMembers.length > 0) {
        await supabase.from('project_members').insert(
          selectedMembers.map((uid) => ({ project_id: project.id, user_id: uid, role: 'member' }))
        );
      }
    }

    setSaving(false);
  };

  const tabs = [
    { id: 'dados', label: 'Dados do Projeto', icon: 'ri-folder-line' },
    { id: 'membros', label: 'Usuários Vinculados', icon: 'ri-team-line' },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-slate-800 font-semibold text-base">{project ? 'Editar Projeto' : 'Novo Projeto'}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex border-b border-slate-200 px-6 shrink-0">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.id ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {activeTab === 'dados' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Cliente <span className="text-red-500">*</span></label>
                  <select value={form.client_id} onChange={(e) => set('client_id', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer">
                    <option value="">Selecionar cliente...</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Nome do projeto <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                    placeholder="Nome do projeto" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label>
                  <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 resize-none"
                    placeholder="Descreva o projeto..." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Responsável interno</label>
                    <select value={form.owner_id} onChange={(e) => set('owner_id', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer">
                      <option value="">Selecionar...</option>
                      {owners.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                    <select value={form.status} onChange={(e) => set('status', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer">
                      <option value="planejamento">Planejamento</option>
                      <option value="ativo">Ativo</option>
                      <option value="pausado">Pausado</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Data de início</label>
                    <input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Previsão de término</label>
                    <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Valor total (opcional)</label>
                    <input type="number" min="0" step="0.01" value={form.total_value} onChange={(e) => set('total_value', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="R$ 0,00" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Horas estimadas (opcional)</label>
                    <input type="number" min="0" value={form.estimated_hours} onChange={(e) => set('estimated_hours', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="0" />
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                  <button type="button" onClick={() => set('scrum_active', !form.scrum_active)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${form.scrum_active ? 'bg-cyan-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.scrum_active ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                  </button>
                  <div>
                    <p className="text-slate-700 text-sm font-medium">Metodologia Scrum ativa</p>
                    <p className="text-slate-400 text-xs">Habilita sprints, backlog e cerimônias Scrum</p>
                  </div>
                </div>

                {!form.scrum_active && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Metodologia</label>
                    <select value={form.methodology} onChange={(e) => set('methodology', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer">
                      <option value="kanban">Kanban</option>
                      <option value="waterfall">Waterfall</option>
                      <option value="agile">Agile</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'membros' && (
              <div>
                {!form.client_id ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <i className="ri-building-line text-3xl mb-2"></i>
                    <p className="text-sm">Selecione um cliente primeiro para ver os usuários disponíveis</p>
                  </div>
                ) : clientUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <i className="ri-team-line text-3xl mb-2"></i>
                    <p className="text-sm">Nenhum usuário vinculado a este cliente</p>
                    <p className="text-xs mt-1">Vincule usuários ao cliente primeiro</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 mb-3">Selecione os usuários do cliente que terão acesso a este projeto:</p>
                    {clientUsers.map((u) => {
                      const checked = selectedMembers.includes(u.id);
                      return (
                        <button key={u.id} type="button" onClick={() => toggleMember(u.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer text-left ${checked ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                          <div className={`w-5 h-5 flex items-center justify-center rounded border shrink-0 transition-colors ${checked ? 'bg-cyan-500 border-cyan-500' : 'border-slate-300'}`}>
                            {checked && <i className="ri-check-line text-white text-xs"></i>}
                          </div>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-100 shrink-0">
                            <i className="ri-user-line text-cyan-600 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-slate-800 text-sm font-medium">{u.full_name}</p>
                            <p className="text-slate-400 text-xs">{u.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer whitespace-nowrap">Cancelar</button>
            <button type="submit" disabled={saving || !form.name.trim() || !form.client_id}
              className="flex items-center gap-2 px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">
              {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              {project ? 'Salvar alterações' : 'Criar projeto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
